import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { login } from '@/api/auth'
import { accountTypeFor, permissionsFor } from '@/config/permissions'
import type { LoginPayload, SessionUser } from '@/types/auth'
import { clearSession, readSession, writeSession } from '@/utils/storage'

export const useAuthStore = defineStore('auth', () => {
  const storedSession = readSession()
  const user = ref<SessionUser | null>(
    storedSession
      ? {
          ...storedSession,
          permissions: permissionsFor(storedSession),
        }
      : null,
  )
  const isAuthenticated = computed(() => Boolean(user.value?.token))

  function hasPermission(permission?: string): boolean {
    return !permission || Boolean(user.value?.permissions.includes(permission))
  }

  async function signIn(payload: LoginPayload): Promise<void> {
    const result = await login(payload)
    const base = { name: result.userName || payload.username, companyId: result.companyId }
    const session: SessionUser = {
      id: result.adminId,
      name: base.name,
      token: result.token,
      companyId: result.companyId,
      accountType: accountTypeFor(base.name),
      permissions: permissionsFor(base),
    }
    user.value = session
    writeSession(session)
  }

  function clear(): void {
    user.value = null
    clearSession()
  }

  return { user, isAuthenticated, hasPermission, signIn, clear }
})
